import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Send, Copy, Check, Share2, MessageCircle, Facebook, Twitter, Mail, Link2 } from "lucide-react";

const SITE_URL = typeof window !== "undefined" ? window.location.origin : "";

export default function InviteFriendsSection() {
  const [name, setName] = useState("");
  const [copied, setCopied] = useState(false);

  const cleanName = (name || "").trim();
  const displayName = cleanName || "Tu nombre";

  // Build the invitation text
  const message = useMemo(() => {
    const intro = cleanName
      ? `Hola, soy ${cleanName}.`
      : `Hola.`;
    return `${intro} Te invito al campamento de mi iglesia Casa de Oración 2026, un campamento de jóvenes llamado "Operación Rescate". La confesión de las Sagradas Escrituras hará que logres un encuentro que transformará tu vida.\n\n📅 28 de agosto, 2026\n✨ No es solo un campamento... es una experiencia divina\n\n¡Inscríbete aquí!\n${SITE_URL}`;
  }, [cleanName]);

  const enc = encodeURIComponent(message);
  const encUrl = encodeURIComponent(SITE_URL);
  const encTitle = encodeURIComponent(`Operación Rescate 2026${cleanName ? " · " + cleanName + " te invita" : ""}`);

  const SHARES = [
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "bg-green-500 hover:bg-green-600", url: `https://wa.me/?text=${enc}` },
    { id: "facebook", label: "Facebook", icon: Facebook, color: "bg-blue-600 hover:bg-blue-700", url: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}&quote=${enc}` },
    { id: "twitter", label: "X / Twitter", icon: Twitter, color: "bg-black hover:bg-gray-800", url: `https://twitter.com/intent/tweet?text=${enc}` },
    { id: "telegram", label: "Telegram", icon: Send, color: "bg-sky-500 hover:bg-sky-600", url: `https://t.me/share/url?url=${encUrl}&text=${enc}` },
    { id: "email", label: "Email", icon: Mail, color: "bg-orange-500 hover:bg-orange-600", url: `mailto:?subject=${encTitle}&body=${enc}` },
  ];

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast.success("Mensaje copiado");
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error("No se pudo copiar");
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Operación Rescate 2026",
          text: message,
          url: SITE_URL,
        });
      } catch (e) {
        // user cancelled
      }
    } else {
      copyText();
    }
  };

  return (
    <section className="relative bg-gradient-to-br from-cyan-50 via-blue-50 to-purple-50 py-24 lg:py-32 px-6 lg:px-12 overflow-hidden">
      <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-200/40 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-200/40 rounded-full blur-3xl" />

      <div className="relative max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white text-xs uppercase tracking-[0.4em] font-bold mb-6">
            <Share2 className="w-3 h-3" />
            Invita a tus amigos
          </span>
          <h2 className="font-heading uppercase text-5xl sm:text-7xl lg:text-8xl text-black leading-[0.9] tracking-tighter">
            Trae a tu<br />
            <span className="text-cyan-600">crew</span> al<br />
            <span className="text-gradient-gold">rescate</span>
          </h2>
          <p className="mt-6 text-lg text-gray-700 max-w-2xl mx-auto">
            La experiencia es 100x más épica si vienes con tus amigos. Genera tu invitación personalizada y compártela.
          </p>
        </motion.div>

        {/* Card with name input + preview + share buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-3xl shadow-2xl p-6 lg:p-10 border border-gray-100"
        >
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Left: Input */}
            <div>
              <Label htmlFor="invite_name" className="text-base font-semibold">
                Tu nombre
              </Label>
              <p className="text-sm text-gray-500 mb-3">Aparecerá en la invitación que recibirán tus amigos.</p>
              <Input
                id="invite_name"
                data-testid="invite-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej: Daniel"
                className="h-14 text-lg"
                maxLength={30}
              />

              <div className="mt-6">
                <Button
                  onClick={nativeShare}
                  size="lg"
                  data-testid="invite-native-share-button"
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold uppercase tracking-wider h-14 text-base"
                >
                  <Share2 className="w-5 h-5 mr-2" />
                  Compartir invitación
                </Button>
                <p className="text-xs text-gray-500 text-center mt-2">En móvil abre el menú nativo de compartir</p>
              </div>

              <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-2">
                {SHARES.map((s) => (
                  <a
                    key={s.id}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`invite-share-${s.id}`}
                    className={`flex flex-col items-center gap-1 p-3 rounded-xl text-white transition-all ${s.color} card-hover`}
                  >
                    <s.icon className="w-5 h-5" />
                    <span className="text-[10px] uppercase tracking-wider font-bold">{s.label}</span>
                  </a>
                ))}
              </div>

              <button
                onClick={copyText}
                data-testid="invite-copy-button"
                className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-gray-300 hover:border-gray-400 rounded-xl text-sm text-gray-700 hover:text-black transition-colors"
              >
                {copied ? <><Check className="w-4 h-4 text-green-600" /> ¡Copiado!</> : <><Link2 className="w-4 h-4" /> Copiar mensaje</>}
              </button>
            </div>

            {/* Right: Preview */}
            <div>
              <Label className="text-base font-semibold">Vista previa</Label>
              <p className="text-sm text-gray-500 mb-3">Así verán tus amigos tu invitación.</p>

              {/* WhatsApp-style preview */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-5 border border-green-100" data-testid="invite-preview">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-orange-600 flex items-center justify-center text-black font-heading text-lg flex-shrink-0">
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-green-800">{displayName}</div>
                    <div className="mt-1 bg-white rounded-2xl rounded-tl-md p-4 shadow-sm">
                      <p className="text-sm text-gray-800 whitespace-pre-line leading-relaxed">
                        {message}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Big personalized title */}
              <motion.div
                key={cleanName}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="mt-4 p-5 bg-black rounded-2xl text-center"
              >
                <div className="text-xs uppercase tracking-widest text-yellow-400 font-bold mb-2">
                  Tu invitación dirá:
                </div>
                <div className="font-heading uppercase text-white text-2xl sm:text-3xl tracking-tight">
                  {cleanName ? (
                    <>
                      <span className="text-gradient-gold">{cleanName}</span> te invita<br />
                      a su <span className="text-orange-400">campamento</span>
                    </>
                  ) : (
                    <span className="text-white/50">Escribe tu nombre arriba</span>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
